import React from 'react';
import SEO from '../components/SEO';

const PrivacyPage = () => {
  return (
    <div style={styles.page}>
      
      <SEO
        title="Privacy Policy"
        description="Driven2WIN's privacy policy — how we collect, use, and protect your information when you interact with our website and services."
        canonicalUrl="https://driven2win.com/privacy"
        noIndex={true}
      />
      
      <div style={styles.container}>
        <h1 style={styles.h1}>PRIVACY POLICY</h1>
        <p style={styles.lastUpdated}>Last Updated: March 2026</p>
        
        <div style={styles.content}>
          <section style={styles.section}>
            <h2 style={styles.h2}>Introduction</h2>
            <p style={styles.p}>
              Driven2WIN ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Information We Collect</h2>
            <p style={styles.p}>We may collect the following types of information:</p>
            <ul style={styles.ul}>
              <li style={styles.li}><strong>Personal Information:</strong> Name, email address, phone number, company name, dealership type, and any information you provide in contact forms or inquiries.</li>
              <li style={styles.li}><strong>Usage Data:</strong> IP address, browser type, pages visited, time spent on pages, and other diagnostic data collected automatically when you visit our website.</li>
              <li style={styles.li}><strong>Cookies and Tracking:</strong> We use cookies and similar tracking technologies to enhance user experience and analyze website traffic.</li>
            </ul>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>How We Use Your Information</h2>
            <p style={styles.p}>We use collected information for the following purposes:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>To respond to your inquiries and provide customer support</li>
              <li style={styles.li}>To deliver services you have requested, including event planning and marketing campaigns</li>
              <li style={styles.li}>To send marketing communications about our services (you may opt-out at any time)</li>
              <li style={styles.li}>To improve our website, services, and customer experience</li>
              <li style={styles.li}>To comply with legal obligations and protect our rights</li>
            </ul>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Information Sharing and Disclosure</h2>
            <p style={styles.p}>
              We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:
            </p>
            <ul style={styles.ul}>
              <li style={styles.li}><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our business (e.g., email marketing platforms, analytics providers).</li>
              <li style={styles.li}><strong>Legal Requirements:</strong> We may disclose information if required by law or in response to valid requests by public authorities.</li>
              <li style={styles.li}><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new entity.</li>
            </ul>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Data Security</h2>
            <p style={styles.p}>
              We implement appropriate technical and organizational security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Your Rights and Choices</h2>
            <p style={styles.p}>You have the right to:</p>
            <ul style={styles.ul}>
              <li style={styles.li}>Access, update, or delete your personal information</li>
              <li style={styles.li}>Opt-out of marketing communications by following unsubscribe instructions in our emails</li>
              <li style={styles.li}>Disable cookies through your browser settings (note: this may affect website functionality)</li>
              <li style={styles.li}>Request a copy of the personal information we hold about you</li>
            </ul>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Third-Party Links</h2>
            <p style={styles.p}>
              Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Children's Privacy</h2>
            <p style={styles.p}>
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Changes to This Privacy Policy</h2>
            <p style={styles.p}>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.h2}>Contact Us</h2>
            <p style={styles.p}>
              If you have any questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <p style={styles.p}>
              <strong>Driven2WIN</strong><br />
              5285 Shad Road, Suite 1619<br />
              Jacksonville, FL 32257<br />
              Phone: 904-638-7200<br />
              Email: info@driven2win.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// Styles
const styles = {
  page: {
    paddingTop: '109px',
    paddingBottom: '60px',
    background: '#FFFFFF',
  },
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '0 16px', // Mobile-first
  },
  h1: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '32px', // Mobile-first (auto-scales)
    color: '#1A1F3A',
    marginBottom: '16px',
    letterSpacing: '2px',
  },
  lastUpdated: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '14px',
    color: '#999',
    marginBottom: '32px',
    fontStyle: 'italic',
  },
  content: {},
  section: {
    marginBottom: '32px',
  },
  h2: {
    fontFamily: 'Bebas Neue, sans-serif',
    fontSize: '24px', // Mobile-first (auto-scales)
    color: '#E31E24',
    marginBottom: '12px',
    letterSpacing: '1px',
  },
  p: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '16px',
    lineHeight: '1.7',
    color: '#333',
    marginBottom: '16px',
  },
  ul: {
    fontFamily: 'Work Sans, sans-serif',
    fontSize: '16px',
    lineHeight: '1.7',
    color: '#333',
    marginLeft: '20px',
    marginBottom: '16px',
  },
  li: {
    marginBottom: '10px',
  },
};

export default PrivacyPage;
